import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import Button from '../../../components/button/Button';

export interface CommitteeBulkActionsBarProps {
  /**
   * Cantidad de carpetas seleccionadas en el listado de comité. Se renderiza
   * en singular si `=== 1` ("1 carpeta seleccionada") y en plural en otro
   * caso ("X carpetas seleccionadas"). Si `=== 0`, los dos botones quedan
   * deshabilitados (no se oculta el bloque para que el usuario sepa qué
   * hacer luego de marcar filas).
   */
  selectedCount: number;
  /**
   * Click en "Aprobar" — Phase 8 abrirá `CommitteeBulkConfirmModal` con
   * `action='APPROVE'` y `count=selectedCount`.
   */
  onApprove: () => void;
  /**
   * Click en "Declinar o solicitar cambios" — Phase 8 abrirá directamente
   * `DeclineOrRequestChangesModal` (sin paso intermedio de confirmación;
   * en bulk el comentario obligatorio ya implica confirmación explícita).
   */
  onReject: () => void;
  /**
   * `true` mientras hay un bulk en `processing` (toast γ activo). Bloquea
   * los dos botones para evitar disparar un segundo bulk en paralelo.
   */
  disabled?: boolean;
}

/**
 * Barra de acciones masivas del listado de comité. Se renderiza al lado del
 * bloque "Editar filtros" del dashboard cuando el usuario tiene rol y el
 * flag `isCommitteeBulkApprovalEnabled === true` (gateo en Phase 8 desde
 * `CommitteeDashboard.tsx` — este componente no lee el flag).
 *
 * Decisiones D1 del design (Engram #59):
 *   - Las filas con `committee_can_approve === false` no exponen checkbox
 *     (lógica que vive en `dashboardColumns.tsx`).
 *   - Task 52098: checkbox maestro en el header de la columna bulk.
 *
 * Tema y branding: usa `Button variant='primary'/'secondary'` ya soportadas
 * por `ThemeContext` para que Cementos / Innova / cliente futuro hereden el
 * branding sin hex hardcoded.
 *
 * Linaje SDD: Engram #59 (design — sección A.6 tema) + #60 (tasks v2) + #70
 * (apply-phase-6 foundation).
 */
const CommitteeBulkActionsBar: React.FC<CommitteeBulkActionsBarProps> = ({
  selectedCount,
  onApprove,
  onReject,
  disabled = false,
}) => {
  const isDisabled = disabled || selectedCount === 0;
  const counterText =
    selectedCount === 1
      ? '1 carpeta seleccionada'
      : `${selectedCount} carpetas seleccionadas`;

  return (
    <Card
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      gap='16px'
      width='100%'
    >
      <P type='2' variant='neutral' weight='bold' withMargin={false}>
        {counterText}
      </P>
      <Card display='flex' gap='12px'>
        <Button
          id='committee-bulk-action-reject'
          variant='secondary'
          size='lg'
          onClick={onReject}
          disabled={isDisabled}
        >
          Declinar o solicitar cambios
        </Button>
        <Button
          id='committee-bulk-action-approve'
          variant='primary'
          size='lg'
          onClick={onApprove}
          disabled={isDisabled}
        >
          Aprobar
        </Button>
      </Card>
    </Card>
  );
};

export default CommitteeBulkActionsBar;
